import { motion } from "framer-motion";
import {
  GraduationCap,
  BookOpen,
  AlertCircle,
  CalendarDays,
  ArrowUpRight,
} from "lucide-react";

import StatCard from "@/components/StatCard";
import { Card, CardHeader, Row } from "@/components/Card";
import { useApp } from "@/App";
import {
  directorStats,
  bursarStats,
  teacherStats,
  studentStats,
  parentStats,
  recentPayments,
  upcomingEvents,
  announcements,
  type Role,
  type Stat,
} from "@/data/mock";
import { school } from "@/theme/school";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — dashboard.
//
// One page, five layouts. The dashboard is the only screen in the
// app that reads the signed-in role and changes structure based
// on it. Everything else — Students, Finance, Brand — is the
// same for everyone (with role-appropriate data filtering that
// will be a later concern).
//
// The role → layout mapping is intentionally hardcoded here
// rather than a registry. There are five roles and five layouts;
// a registry would be more code than the switch it replaces.
// When the sixth role arrives, extract.
// ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const role = useApp((s) => s.role);
  const name = useApp((s) => s.name);

  const greeting = getGreeting();
  const firstName = name?.split(" ")[0] ?? "";

  return (
    <div className="px-5 pb-8 pt-5">
      {/* ─── Greeting ──────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mb-5"
      >
        <p className="text-ios-footnote text-ios-secondary">
          {greeting}, {firstName}
        </p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold tracking-ios text-ios-label">
          {titleFor(role)}
        </h1>
      </motion.div>

      {/* ─── Stats grid ────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        {statsFor(role).map((s, i) => (
          <StatCard
            key={s.label}
            index={i}
            label={s.label}
            value={s.value}
            caption={s.caption}
            delta={s.delta}
            icon={s.icon}
            tone={s.tone}
            spark={s.spark}
          />
        ))}
      </div>

      {/* ─── Role-specific middle block ────────────────────── */}
      {role === "director" && <DirectorBlock />}
      {role === "bursar" && <BursarBlock />}
      {role === "teacher" && <TeacherBlock />}
      {(role === "student" || role === "parent") && <FamilyBlock role={role} />}

      {/* ─── Announcements ───────────────────────────────────
          Shared across all roles. A director's memo about
          term dates matters to a parent too. */}
      <Card className="mt-5" index={3}>
        <CardHeader
          title="Announcements"
          subtitle={`${announcements.length} this week`}
          trailing={
            <span className="grid h-6 w-6 place-items-center rounded-full bg-ios-fill text-ios-secondary">
              <ArrowUpRight size={13} strokeWidth={2.4} absoluteStrokeWidth />
            </span>
          }
        />
        <div className="px-4 pb-2">
          {announcements.slice(0, 3).map((a, i) => (
            <div
              key={a.id}
              className={[
                "flex items-start gap-3 py-2.5",
                i < 2 ? "hairline-b" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span
                className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
                style={{
                  backgroundColor: a.urgent ? "#FF3B30" : "var(--brand-navy)",
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-ios-subhead font-semibold text-ios-label">
                  {a.title}
                </p>
                <p className="mt-0.5 line-clamp-2 text-ios-caption-1 text-ios-secondary">
                  {a.body}
                </p>
              </div>
              <span className="shrink-0 text-ios-caption-2 text-ios-tertiary">
                {a.when}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* ─── Upcoming events ────────────────────────────────
          Shared across all roles. A term calendar is a term
          calendar. */}
      <Card className="mt-5" index={4}>
        <CardHeader title="Upcoming" subtitle="Term 2, 2026" />
        <div>
          {upcomingEvents.map((e, i) => (
            <Row
              key={e.id}
              title={e.title}
              subtitle={e.where}
              leading={CalendarDays}
              leadingTone={e.tone}
              trailing={e.when}
              chevron
              separator={i < upcomingEvents.length - 1}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Role-specific blocks ───────────────────────────────────────

function DirectorBlock() {
  return (
    <Card className="mt-5" index={2}>
      <CardHeader
        title="Recent payments"
        subtitle="Last 24 hours"
        trailing={
          <span className="text-ios-caption-1 font-semibold" style={{ color: "var(--brand-navy)" }}>
            View all
          </span>
        }
      />
      <div>
        {recentPayments.slice(0, 4).map((p, i) => (
          <Row
            key={p.id}
            title={p.student}
            subtitle={`${p.guardian} · ${p.method}`}
            leading={(
              <span
                className="grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold"
                style={{ backgroundColor: "rgba(52, 199, 89, 0.14)", color: "#248A3D" }}
              >
                {p.student
                  .split(" ")
                  .map((x) => x[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </span>
            )}
            trailing={
              <span className="font-tabular font-semibold text-ios-label">
                {p.amount}
              </span>
            }
            separator={i < 3}
          />
        ))}
      </div>
    </Card>
  );
}

function BursarBlock() {
  return (
    <Card className="mt-5" index={2}>
      <CardHeader title="Outstanding fees" subtitle="Top 4 by balance" />
      <div>
        {recentPayments.slice(0, 4).map((p, i) => (
          <Row
            key={p.id}
            title={p.student}
            subtitle={`Term 2 · due ${p.due}`}
            leading={AlertCircle}
            leadingTone={i % 2 === 0 ? "red" : "orange"}
            trailing={
              <span className="font-tabular font-semibold text-ios-label">
                {p.balance}
              </span>
            }
            chevron
            separator={i < 3}
          />
        ))}
      </div>
    </Card>
  );
}

function TeacherBlock() {
  return (
    <Card className="mt-5" index={2}>
      <CardHeader title="Today's classes" subtitle="4 periods" />
      <div>
        {school.timetable.map((t, i) => (
          <Row
            key={t.id}
            title={t.subject}
            subtitle={`${t.klass} · ${t.room}`}
            leading={GraduationCap}
            leadingTone="brand"
            trailing={t.time}
            separator={i < school.timetable.length - 1}
          />
        ))}
      </div>
    </Card>
  );
}

function FamilyBlock({ role }: { role: Role }) {
  if (role === "student") {
    return (
      <Card className="mt-5" index={2}>
        <CardHeader title="My subjects" subtitle="Term 2 progress" />
        <div>
          {["Mathematics", "Physics", "English", "Biology"].map((subject, index, subjects) => (
            <Row
              key={subject}
              title={subject}
              subtitle={index === 0 ? "Next assessment in 6 days" : "Latest mark published"}
              leading={BookOpen}
              leadingTone={index === 0 ? "brand" : "blue"}
              trailing={index === 0 ? "76%" : `${72 + index * 2}%`}
              chevron
              separator={index < subjects.length - 1}
            />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-5" index={2}>
      <CardHeader
        title={role === "parent" ? "My children" : "My subjects"}
        subtitle={role === "parent" ? "2 enrolled" : "8 this term"}
      />
      <div>
        {school.children.map((c, i) => (
          <Row
            key={c.name}
            title={c.name}
            subtitle={`${c.klass} · ${c.status}`}
            leading={(
              <span
                className="grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold"
                style={{ backgroundColor: "rgba(10, 37, 64, 0.08)", color: "var(--brand-navy)" }}
              >
                {c.name
                  .split(" ")
                  .map((x) => x[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </span>
            )}
            trailing={
              <span
                className="font-tabular text-ios-footnote font-semibold"
                style={{ color: c.status === "Paid" ? "#248A3D" : "#C93400" }}
              >
                {c.balance}
              </span>
            }
            chevron
            separator={i < school.children.length - 1}
          />
        ))}
      </div>
    </Card>
  );
}

// ─── Data selectors ─────────────────────────────────────────────

function statsFor(role: Role | null): Stat[] {
  switch (role) {
    case "director":
      return directorStats;
    case "bursar":
      return bursarStats;
    case "teacher":
      return teacherStats;
    case "student":
      return studentStats;
    case "parent":
      return parentStats;
    default:
      return directorStats;
  }
}

function titleFor(role: Role | null): string {
  switch (role) {
    case "director": return "School overview";
    case "bursar":   return "Finance desk";
    case "teacher":  return "Your day";
    case "student":  return "Your studies";
    case "parent":   return "Your family";
    default:         return "Overview";
  }
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

